import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { BACKEND_URL } from "./config";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const HTML_ENTITIES = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

const SOURCE_CODE_LABELS = {
  g1: 'G1',
  r7: 'R7',
  'cnn-brasil': 'CNN Brasil',
};

const KNOWN_SOURCE_SUFFIXES = new Set([
  'cnn brasil',
  'cnn bresil',
  'cnn brazil',
  'g1',
  'r7',
  'record',
  'globo',
  'uol',
  'terra',
  'metropoles',
  'métropoles',
  'bbc news brasil',
  'bbc brasil',
  'folha de s paulo',
  'folha',
  'estadao',
  'estadão',
  'makaya enfo',
  'makayaenfo',
  'me hayti',
]);

export function toPlainText(value) {
  if (value === null || value === undefined) return '';

  const raw = String(value);
  const withoutTags = raw.replace(/<[^>]*>/g, ' ');
  const decoded = withoutTags.replace(/&[a-zA-Z0-9#]+;/g, (entity) => HTML_ENTITIES[entity.toLowerCase()] || entity);

  return decoded.replace(/\s+/g, ' ').trim();
}

function normalizeSourceToken(value) {
  return toPlainText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function getArticleSourceName(article = {}) {
  const candidates = [
    article?.source_name,
    article?.source_publisher,
    article?.publisher,
    SOURCE_CODE_LABELS[String(article?.source_code || '').trim().toLowerCase()] || '',
  ];

  const authorName = toPlainText(article?.author_name || '');
  if (/^via\s+/i.test(authorName)) {
    candidates.push(authorName.replace(/^via\s+/i, '').trim());
  }

  return candidates.map(toPlainText).find(Boolean) || '';
}

export function cleanArticleTitle(title, article = {}) {
  const plainTitle = toPlainText(title);
  if (!plainTitle) return '';

  const sourceName = getArticleSourceName(article);
  const sourceCode = String(article?.source_code || '').trim().toLowerCase();
  const trailingMatch = plainTitle.match(/\s*[|\-–—]\s*([^|\-–—]{2,80})\s*$/);

  if (!trailingMatch) return plainTitle;

  const trailingSegment = toPlainText(trailingMatch[1]);
  const normalizedTrailing = normalizeSourceToken(trailingSegment);
  const normalizedSource = normalizeSourceToken(sourceName);
  const hasSourceContext = Boolean(normalizedSource || sourceCode);
  const shouldStrip =
    hasSourceContext ||
    (normalizedTrailing && KNOWN_SOURCE_SUFFIXES.has(normalizedTrailing));

  if (!shouldStrip) return plainTitle;

  const strippedTitle = plainTitle.slice(0, trailingMatch.index).trim();
  return strippedTitle.length >= 18 ? strippedTitle : plainTitle;
}

export function resolveMediaUrl(value) {
  if (value === null || value === undefined) return '';

  const raw = String(value).trim();
  if (!raw) return '';

  if (/^(?:https?:|data:|blob:)/i.test(raw)) return raw;
  if (raw.startsWith('//')) {
    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
    return `${protocol}${raw}`;
  }

  const base = String(BACKEND_URL || '').replace(/\/+$/, '');
  const normalizedPath = raw.startsWith('/') ? raw : `/${raw}`;

  return base ? `${base}${normalizedPath}` : normalizedPath;
}

export function getArticleCoverImages(article = {}) {
  const rawImages = [
    article?.cover_image,
    ...(Array.isArray(article?.cover_images) ? article.cover_images : []),
  ];
  const seen = new Set();

  return rawImages
    .map(resolveMediaUrl)
    .filter(Boolean)
    .filter((image) => {
      if (seen.has(image)) return false;
      seen.add(image);
      return true;
    })
    .slice(0, 2);
}

export function normalizeExternalUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^(https?:|mailto:|tel:)/i.test(raw)) return raw;
  if (raw.startsWith('//')) return `https:${raw}`;
  return `https://${raw}`;
}
